import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUnreadMessages } from '@/contexts/UnreadMessagesContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  FolderOpen,
  Users,
  MessageSquare,
  FileText,
  Shield,
  UserCircle,
  LogOut,
  Menu,
  X,
  Globe,
  CheckSquare,
  ExternalLink,
  TrendingUp,
  Clock,
  Briefcase,
  DollarSign,
  Wallet,
  Bell
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { unreadCount } = useUnreadMessages();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: t('dashboard'), href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'employee', 'client'] },
    { name: '📁 Mon Dossier', href: '/mon-dossier', icon: FolderOpen, roles: ['client'] },
    { name: t('files'), href: '/files', icon: FolderOpen, roles: ['admin', 'employee'] },
    { name: t('employees'), href: '/employees', icon: Users, roles: ['admin'] },
    { name: '🔐 Gestion des Rôles', href: '/role-management', icon: Shield, roles: ['admin'], superAdminOnly: true },
    { name: t('clients'), href: '/clients', icon: Users, roles: ['admin', 'employee'] },
    { name: 'Clients Actifs', href: '/clients-list', icon: Users, roles: ['admin'] },
    { name: 'Groupes de Clients', href: '/client-groups', icon: Users, roles: ['admin', 'subadmin', 'employee'] },
    { name: t('messages'), href: '/messages', icon: MessageSquare, roles: ['admin', 'employee', 'client'] },
    { name: '🔐 Permissions Messages', href: '/messaging-permissions', icon: Shield, roles: ['admin'] },
    { name: 'Devis', href: '/quotes', icon: FileText, roles: ['admin', 'employee', 'client'] },
    { name: t('invoices'), href: '/invoices', icon: FileText, roles: ['admin', 'employee', 'client'] },
    { name: '💳 Paiements', href: '/payments', icon: DollarSign, roles: ['admin', 'sub_admin', 'employee', 'client'] },
    { name: 'Pointage', href: '/timesheet', icon: Clock, roles: ['admin', 'employee'] },
    { name: '🏢 Gestion RH', href: '/rh', icon: Briefcase, roles: ['admin', 'sub_admin', 'employee'] },
    { name: '💰 Gestion Paie', href: '/gestion-paie', icon: DollarSign, roles: ['admin'], employeeRoles: ['comptabilite'] },
    { name: '🏦 Trésorerie', href: '/tresorerie', icon: Wallet, roles: ['admin'], employeeRoles: ['comptabilite'] },
    { name: '💼 Compte Client', href: '/compte-client', icon: Briefcase, roles: ['admin'] },
    { name: 'Approbations', href: '/document-approvals', icon: CheckSquare, roles: ['admin'] },
    { name: t('audit'), href: '/audit', icon: Shield, roles: ['admin'] },
  ];

  const filteredNavigation = navigation.filter(item => {
    // Check role first
    if (!item.roles.includes(user?.role)) {
      // If not in main roles, check if it requires specific employee roles
      if (item.employeeRoles && user?.employee_roles) {
        const hasRequiredRole = item.employeeRoles.some(role => user.employee_roles.includes(role));
        if (!hasRequiredRole) return false;
      } else {
        return false;
      }
    }
    
    // If item requires super admin, check that too
    if (item.superAdminOnly && !user?.is_super_admin) return false;
    
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-red-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-white/95 backdrop-blur-xl border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <img
                src="/logo.png"
                alt="LCS Case Management"
                className="h-12 w-auto"
              />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              data-testid="close-sidebar-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              const isMessagesItem = item.href === '/messages';
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.href.substring(1)}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all relative ${
                    isActive
                      ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <div className="relative">
                    <item.icon className="h-5 w-5" />
                    {isMessagesItem && unreadCount > 0 && (
                      <span className="absolute -top-2 -right-2 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </div>
                  <span className="flex-1">{item.name}</span>
                  {isMessagesItem && unreadCount > 0 && (
                    <span className={`flex items-center justify-center min-w-[24px] h-[24px] px-2 text-xs font-bold rounded-full ${
                      isActive ? 'bg-white text-red-600' : 'bg-red-500 text-white'
                    }`}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Link>
              );
            })}
            
            {/* Applications Intégrées - Quick Links */}
            {(user?.role === 'admin' || user?.role === 'employee') && (
              <div className="pt-4 mt-4 border-t border-gray-200">
                <p className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Outils de Gestion
                </p>
                
                <Link
                  to="/workflow-tracker"
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
                    location.pathname === '/workflow-tracker'
                      ? 'bg-gradient-to-r from-green-600 to-blue-600 text-white shadow-lg'
                      : 'text-gray-700 hover:bg-gradient-to-r hover:from-green-50 hover:to-blue-50'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <TrendingUp className="h-5 w-5" />
                  <span className="flex-1">Suivi Workflow</span>
                </Link>
              </div>
            )}
          </nav>

          {/* User info */}
          <div className="px-4 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-red-500 to-blue-600 flex items-center justify-center text-white font-bold">
                  {user?.full_name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{user?.full_name}</p>
                  <p className="text-xs text-gray-500 truncate">{t(user?.role)}</p>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleLanguage}
                className="flex-1"
                data-testid="language-toggle-btn"
              >
                <Globe className="h-4 w-4 mr-2" />
                {i18n.language.toUpperCase()}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/profile')}
                data-testid="profile-btn"
              >
                <UserCircle className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72 relative">
        {/* Background Logo Watermark */}
        <div className="fixed inset-0 lg:left-72 pointer-events-none z-0 flex items-center justify-center overflow-hidden">
          <img
            src="/images/image0.jpg"
            alt="LCS Background"
            className="opacity-15 w-1/2 h-auto object-contain"
          />
        </div>

        {/* Mobile header */}
        <div className="lg:hidden flex items-center justify-between px-4 py-4 bg-white/95 backdrop-blur-xl border-b border-gray-200 sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100"
            data-testid="open-sidebar-btn"
          >
            <Menu className="h-6 w-6" />
          </button>
          <img
            src="/logo.png"
            alt="LCS Case Management"
            className="h-10 w-auto"
          />
          {/* Notification bell for mobile */}
          <button
            onClick={() => navigate('/messages')}
            className="p-2 rounded-lg hover:bg-gray-100 relative"
            data-testid="mobile-notification-btn"
          >
            <Bell className="h-6 w-6 text-gray-600" />
            {unreadCount > 0 && (
              <span className="absolute top-0 right-0 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full animate-pulse">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* Page content */}
        <main className="p-6 lg:p-8 relative z-10">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;